/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { AppBar, createStyles, IconButton, ListItem, ListItemText, makeStyles, Theme, Toolbar, Typography } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import React, { useMemo, useRef, useState, useLayoutEffect, CSSProperties } from "react";
import { useSelector } from "react-redux";
import { FixedSizeList as List } from "react-window";
import { createSelector } from 'reselect';
import { Coop } from "../../db/stores/coops";
import { Facility } from "../../db/stores/facilities";
import { AppState } from "../../redux";
import Search from "./Search";

export interface FacilityQueryParams {
  program?: string;
  program_coop?: number;
  load?: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flex: 1,
      // height: "calc(100% + 96px)",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    },
    toolbarButton: {
      marginRight: theme.spacing(2),
    },
    toolbarTitle: {
      marginRight: theme.spacing(4),
      flexShrink: 0,
    },
    search: {
      maxWidth: theme.breakpoints.width("sm"),
      width: "100%",
    },
    list: {
      height: "calc(100% + 96px)",
      WebkitOverflowScrolling: "touch",
    },
  })
);

interface FacilitySelectProps {
  onSelect: (facility: Facility | null) => void;
  params: FacilityQueryParams;
}

function facilitySearchFilter(text: string) {
  // TODO: Improve performance for this comparison
  const exp = new RegExp(text, "i");
  return function (facility: Facility) {
    return (
      exp.test(facility.name) ||
      (facility.premise_id && exp.test(facility.premise_id)) ||
      (facility.customIds && exp.test(JSON.stringify(facility.customIds))) ||
      (exp.test(facility.farm?.name)) ||  // Optional queries on these lines shouldn't be necessary but dev data is contaminated
      (facility.farm?.customIds && exp.test(JSON.stringify(facility.farm.customIds)))
    );
  };
}

/**
 * Only show facilities that are either enrolled in the specified program, or belong to a coop that is enrolled in the specified program
 *
 * @param program 
 * @param program_coop 
 * @returns 
 */
const selectFacilitiesWithCoops = (program: string | undefined, program_coop: number | undefined) => createSelector(
  (state: AppState) => Object.values(state.facilities.items),
  (state: AppState) => Object.values(state.coops.items),
  (facilities, coops) => {
    if (!program && !program_coop) {
      return facilities.sort((a, b) => a.name.localeCompare(b.name));
    }
    return facilities.filter(f => {
      if (program) {
        if (f.programs?.includes(program)) {
          return true;
        }

        if (!program_coop) {
          return false;
        }
      }

      if (program_coop) {
        const farmCoops: Coop[] = coops.filter((c: Coop) => {
          if (f?.farm) {
            if (!c.farms!.includes(f.farm.id)) {
              return false;
            }
            if (program_coop === 2) {
              return c.is_environmental_participant;
            }
            if (program_coop === 3) {
              return c.is_wfd_participant;
            }
          }
          return false;
        });

        return farmCoops.length;
      }
    })
    .sort((a, b) => a.name.localeCompare(b.name));
  }
);
const ITEM_SIZE = 52;

export default (props: FacilitySelectProps) => {
  const classes = useStyles(props);
  const facilities = useSelector<AppState, Facility[]>(selectFacilitiesWithCoops(props.params?.program, props.params?.program_coop));

  const coops = useSelector<AppState, Coop[]>((state: AppState) => {
    return Object.values(state.coops.items);
  });

  const [filter, setFilter] = useState<string>("");
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null);

  const filteredFacilities = useMemo(() => facilities.filter(facilitySearchFilter(filter)), [facilities, filter]);
  const [filteredCoops, setFilteredCoops] = useState<Coop[]>([]);

  const rootRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    if (rootRef.current) {
      setHeight((rootRef.current as any).clientHeight);
    }
  }, [height])
  function renderFacilities() {
    return (
      <div className={classes.root}>
        <AppBar position="sticky">
          <Toolbar color="primary" variant="dense">
            <IconButton
              className={classes.toolbarButton}
              edge="start"
              color="inherit"
              onClick={() => props.onSelect(null)}
            >
              <CloseIcon />
            </IconButton>
            <Typography className={classes.toolbarTitle} variant="h6">
              Select facility
            </Typography>
            <Search className={classes.search} onSearchUpdated={setFilter} showClear />
          </Toolbar>
        </AppBar>

        <div className={classes.list} ref={rootRef}>
          <List
            itemSize={ITEM_SIZE}
            itemCount={filteredFacilities.length}
            height={height}
            width="100%"
          >
            {({ index, style }) => {
              const f = JSON.parse(JSON.stringify(filteredFacilities[index]));  // copy since we are going to mutate
              const ids: string[] = [];
              if (f.premise_id) ids.push(f.premise_id);
              if (f.customIds) ids.concat(f.customIds);

              return (
                <ListItem style={style as CSSProperties} key={f.name} button onClick={() => {
                  const farmCoops: Coop[] = coops.filter((c: Coop) => {
                    return c.farms!.includes(f.farm!.id);
                  });
                  if (farmCoops.length > 1) {
                    setSelectedFacility(f);
                    setFilteredCoops(farmCoops.sort((a, b) => a.name.localeCompare(b.name)));
                    return;
                  }
                  f.coop = (farmCoops.length === 1) ? farmCoops[0] : undefined;
                  props.onSelect(f);
                }}>
                  <ListItemText
                    primary={f.name}
                    secondary={ids.join(" | ")}
                  />
                </ListItem>
              );
            }}
          </List>
        </div>
      </div>
    );
  }

  function renderCoops() {
    return (
      <div className={classes.root}>
        <AppBar position="sticky">
          <Toolbar color="primary" variant="dense">
            <IconButton
              className={classes.toolbarButton}
              edge="start"
              color="inherit"
              onClick={() => props.onSelect(null)}
            >
              <CloseIcon />
            </IconButton>
            <Typography className={classes.toolbarTitle} variant="h6">
              Select Participant
            </Typography>
          </Toolbar>
        </AppBar>

        <div className={classes.list} ref={rootRef}>
          <List
            itemSize={ITEM_SIZE}
            itemCount={filteredCoops.length}
            height={height}
            width="100%"
          >
            {({ index, style }) => {
              const c = filteredCoops[index];
              return (
                <ListItem style={style as CSSProperties} key={c.name} button onClick={() => {
                  selectedFacility!.coop = c;
                  props.onSelect(selectedFacility!);
                }}>
                  <ListItemText
                    primary={c.name}
                  />
                </ListItem>
              );
            }}
          </List>
        </div>
      </div>
    );
  }

  return !selectedFacility ? renderFacilities() : renderCoops();
};
